import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, MapPin, Users, Eye, DollarSign, Target } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface RegionalData {
  region: string;
  kolCount: number;
  totalPosts: number;
  totalViews: number;
  totalExpense: number;
  avgCpv: number;
  kols: Array<{
    id: string;
    name: string;
    platform: string;
    postCount: number;
    views: number;
    expense: number;
    tier?: string;
  }>;
}

interface RegionalHierarchicalTableProps {
  data: any[];
  isLoading?: boolean;
}

export const RegionalHierarchicalTable = ({ data, isLoading }: RegionalHierarchicalTableProps) => {
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const [processedData, setProcessedData] = useState<RegionalData[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const processRegionalData = async () => {
      if (!data || data.length === 0) {
        setProcessedData([]);
        return;
      }

      setIsProcessing(true);

      try {
        // Get unique kol_db_ids from the data
        const kolDbIds = [...new Set(data.map(item => item.kol_db_id).filter(Boolean))];
        
        if (kolDbIds.length === 0) {
          setProcessedData([]);
          return;
        }

        // Fetch region information from kol_db for all unique kol_db_ids
        const { data: kolDbData, error } = await supabase
          .from('kol_db')
          .select('id, region')
          .in('id', kolDbIds);

        if (error) {
          console.error('Error fetching kol_db regions:', error);
          setProcessedData([]);
          return;
        }

        // Create a map of kol_db_id to region
        const kolRegionMap = new Map<string, string>();
        kolDbData?.forEach(item => {
          kolRegionMap.set(item.id, item.region || 'Unknown Region');
        });

        // Group data by region (from kol_db) and then by KOL
        const regionMap = new Map<string, Map<string, any>>();

        data.forEach(item => {
          const kolId = item.kol_db_id;
          const region = kolRegionMap.get(kolId) || 'Unknown Region';
          
          if (!regionMap.has(region)) {
            regionMap.set(region, new Map());
          }
          
          const regionKols = regionMap.get(region)!;
          
          if (!regionKols.has(kolId)) {
            regionKols.set(kolId, {
              id: kolId,
              name: item.name || 'Unknown KOL',
              platform: item.platform || 'Unknown',
              tier: item.tier,
              posts: [],
              totalViews: 0,
              totalExpense: 0,
              postCount: 0
            });
          }
          
          const kol = regionKols.get(kolId)!;
          kol.posts.push(item);
          kol.totalViews += item.content_views || 0;
          kol.totalExpense += item.expense || 0;
          kol.postCount += 1;
        });

        // Convert to regional data structure
        const regionalData: RegionalData[] = [];
        
        regionMap.forEach((kols, region) => {
          const kolArray = Array.from(kols.values()).map(kol => ({
            id: kol.id,
            name: kol.name,
            platform: kol.platform,
            postCount: kol.postCount,
            views: kol.totalViews,
            expense: kol.totalExpense,
            tier: kol.tier
          }));
          
          const totalViews = kolArray.reduce((sum, kol) => sum + kol.views, 0);
          const totalExpense = kolArray.reduce((sum, kol) => sum + kol.expense, 0);
          const totalPosts = kolArray.reduce((sum, kol) => sum + kol.postCount, 0);
          
          regionalData.push({
            region,
            kolCount: kolArray.length,
            totalPosts,
            totalViews,
            totalExpense,
            avgCpv: totalViews > 0 ? totalExpense / totalViews : 0,
            kols: kolArray.sort((a, b) => b.views - a.views)
          });
        });
        
        setProcessedData(regionalData.sort((a, b) => b.totalViews - a.totalViews));
      } catch (error) {
        console.error('Error processing regional data:', error);
        setProcessedData([]);
      } finally {
        setIsProcessing(false);
      }
    };

    processRegionalData();
  }, [data]);

  const toggleRegion = (region: string) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(region)) {
      newExpanded.delete(region);
    } else {
      newExpanded.add(region);
    }
    setExpandedRegions(newExpanded);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(Math.round(num));
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  if (isLoading || isProcessing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Regional Performance Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-48">
            <p className="text-muted-foreground">Loading regional data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (processedData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Regional Performance Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-48">
            <p className="text-muted-foreground">No regional data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Regional Performance Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-7 gap-4 p-4 bg-muted/50 border-b font-medium text-sm">
            <div className="flex items-center gap-2">
              <span>Region</span>
            </div>
            <div className="flex items-center gap-1 justify-center">
              <Users className="h-4 w-4" />
              <span>KOLs</span>
            </div>
            <div className="flex items-center gap-1 justify-center">
              <Target className="h-4 w-4" />
              <span>Posts</span>
            </div>
            <div className="flex items-center gap-1 justify-center">
              <Eye className="h-4 w-4" />
              <span>Views</span>
            </div>
            <div className="flex items-center gap-1 justify-center">
              <DollarSign className="h-4 w-4" />
              <span>Expense</span>
            </div>
            <div className="text-center">Avg CPV</div>
            <div className="text-center">Actions</div>
          </div>

          {/* Data Rows */}
          {processedData.map((regionData) => (
            <div key={regionData.region}>
              {/* Region Row */}
              <div className="grid grid-cols-7 gap-4 p-4 hover:bg-muted/30 border-b">
                <div className="font-medium text-foreground">
                  {regionData.region}
                </div>
                <div className="text-center font-medium">
                  {regionData.kolCount}
                </div>
                <div className="text-center">
                  {formatNumber(regionData.totalPosts)}
                </div>
                <div className="text-center">
                  {formatNumber(regionData.totalViews)}
                </div>
                <div className="text-center">
                  {formatCurrency(regionData.totalExpense)}
                </div>
                <div className="text-center">
                  ${regionData.avgCpv.toFixed(4)}
                </div>
                <div className="text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleRegion(regionData.region)}
                    className="h-6 w-6 p-0"
                  >
                    {expandedRegions.has(regionData.region) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* KOL Rows (when expanded) */}
              {expandedRegions.has(regionData.region) && (
                <div className="bg-muted/20">
                  {regionData.kols.map((kol) => (
                    <div key={kol.id} className="grid grid-cols-7 gap-4 p-4 pl-8 hover:bg-muted/40 border-b border-border/50">
                      <div className="text-sm text-muted-foreground">
                        <div className="font-medium">{kol.name}</div>
                        <div className="text-xs">{kol.platform} {kol.tier && `• ${kol.tier}`}</div>
                      </div>
                      <div className="text-center text-sm">1</div>
                      <div className="text-center text-sm">
                        {formatNumber(kol.postCount)}
                      </div>
                      <div className="text-center text-sm">
                        {formatNumber(kol.views)}
                      </div>
                      <div className="text-center text-sm">
                        {formatCurrency(kol.expense)}
                      </div>
                      <div className="text-center text-sm">
                        ${kol.views > 0 ? (kol.expense / kol.views).toFixed(4) : '0.0000'}
                      </div>
                      <div></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};